import axios from "axios";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import "../styles/StudentDashboard.css";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const API = "https://stu168523.webhosting.arden.ac.uk";

export default function StudentDashboard() {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [currentResult, setCurrentResult] = useState(null);
  const [history, setHistory] = useState([]);

  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const userName = localStorage.getItem("userName") || "Student";

  /* ================= LOAD CATEGORIES ================= */
  useEffect(() => {
    axios.get(API + "/api/categories").then((res) => setCategories(res.data));
  }, []);

  const logout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  /* ================= LOAD HISTORY ALWAYS ================= */
  const loadHistory = () => {
    axios
      .get(`${API}/api/results/student/${userId}`)
      .then((res) => setHistory(res.data))
      .catch(() => setHistory([]));
  };

  useEffect(() => {
    loadHistory();
  }, [userId]);

  /* ================= LOAD QUESTIONS ================= */
  const loadQuestions = async (category) => {
    setSelectedCategory(category);
    setCurrentResult(null);
    setAnswers([]);

    const res = await axios.get(
      `${API}/api/questions/category/${category._id}`
    );
    setQuestions(res.data);
  };

  /* ================= SUBMIT QUIZ ================= */
  const submitQuiz = async () => {
    const res = await axios.post(
      `${API}/api/results/submit`,
      {
        categoryId: selectedCategory._id,
        answers,
      },
      { headers: { authorization: token } }
    );

    setCurrentResult(res.data);
    loadHistory(); // refresh history immediately
  };

  /* ================= CHART DATA ================= */
  const chartData = {
    labels: history.map((h) => h.categoryId.name),
    datasets: [
      {
        label: "Quiz Scores",
        data: history.map((h) => h.score),
      },
    ],
  };

  /* ================= RENDER ================= */
  return (
    <div className="student-dashboard">
      {/* ===== STUDENT NAVBAR ===== */}
      <div className="student-navbar">
        <div className="student-logo">🎓 QuizLMS</div>

        <div className="student-nav-right">
          <span className="student-name">👤 {userName}</span>

          <button className="logout-btn" onClick={logout}>
            Logout
          </button>
        </div>
      </div>

      {/* ===== CATEGORIES ===== */}
      <h3 className="section-title">Select Quiz Category</h3>
      <div className="category-grid">
        {categories.map((cat) => (
          <div
            key={cat._id}
            className={`category-card ${
              selectedCategory?._id === cat._id ? "active" : ""
            }`}
            onClick={() => loadQuestions(cat)}
          >
            {cat.name}
          </div>
        ))}
      </div>

      {/* ===== QUIZ ===== */}
      {questions.length > 0 && (
        <>
          <h3 className="section-title">{selectedCategory.name} Quiz</h3>

          <div className="quiz-container">
            {questions.map((q, i) => (
              <div className="quiz-card" key={q._id}>
                <h4>{q.questionText}</h4>

                {q.options.map((opt, idx) => (
                  <label key={idx} className="option">
                    <input
                      type="radio"
                      name={`q${i}`}
                      onChange={() => {
                        const a = [...answers];
                        a[i] = idx;
                        setAnswers(a);
                      }}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            ))}

            <button className="primary-btn" onClick={submitQuiz}>
              Submit Quiz
            </button>
          </div>
        </>
      )}

      {/* ===== CURRENT QUIZ RESULT (ONLY LATEST) ===== */}
      {currentResult && (
        <div className="result-card">
          <h3>Latest Quiz Result</h3>
          <p>
            Score: <strong>{currentResult.score}</strong> /{" "}
            {currentResult.totalQuestions}
          </p>
        </div>
      )}

      {/* ===== HISTORY & ANALYTICS (ALWAYS VISIBLE) ===== */}
      <div className="history-section">
        <h3>Your Quiz History</h3>

        {history.length === 0 ? (
          <p className="empty-text">
            You haven’t attempted any quizzes yet. Select a category above to get
            started.
          </p>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Score</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h._id}>
                    <td>{h.categoryId.name}</td>
                    <td>
                      {h.score}/{h.totalQuestions}
                    </td>
                    <td>
                      {new Date(h.attemptedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3>Performance Analytics</h3>
            <Bar data={chartData} />
          </>
        )}
      </div>
    </div>
  );
}
